/**
 * Selectors for the /admin/app-config definition sync.
 *
 * `selectDefinitionDiff` is where the manifest meets the environment. It is
 * memoised on the live records alone, so re-rendering the page does not re-run
 * the diff, and the page never computes buckets for itself.
 *
 * `selectVeritoneAppId` lives here rather than in the service layer so the card
 * can read the app id without importing `callGraphQLApi` — and with it all of
 * `callApi`'s glc-redux surface — just to print it in a header.
 */
import { createSelector } from 'reselect';
import { modules } from '@veritone/glc-redux';
import { FALLBACK_VERITONE_APP_ID } from '@helpers/constants';
import { AdminState, DefinitionDiff, RootOrgStatus } from './models';
import { diffDefinitions } from './models/diffDefinitions';
import { CONFIG_DEFINITION_MANIFEST } from './models/manifest';
import { AdminStore, defaultState, namespace } from './store';

const {
  config: { getConfig },
} = modules;

/**
 * The Redact app id this environment is configured with.
 *
 * Lives here rather than in the service layer so the card can read it without
 * importing `callGraphQLApi` — and with it the whole of `callApi`'s glc-redux
 * surface — just to print an id in a header.
 */
export const selectVeritoneAppId = (state: unknown): string => {
  const config = getConfig(state as never) as { veritoneAppId?: string };
  return config?.veritoneAppId || FALLBACK_VERITONE_APP_ID;
};

export const selectStore = (state: object): AdminState =>
  (state as Partial<AdminStore>)[namespace] || defaultState;

export const selectIsScanning = createSelector(
  selectStore,
  (s) => s.isScanning
);

/**
 * True once a scan has succeeded. Consumers must gate on this before reading
 * the diff: before it, "nothing missing" and "we have not looked" are the
 * same empty result.
 */
export const selectHasScanned = createSelector(
  selectStore,
  (s) => s.hasScanned
);

export const selectScanError = createSelector(selectStore, (s) => s.scanError);

export const selectScannedAt = createSelector(selectStore, (s) => s.scannedAt);

export const selectLiveDefinitions = createSelector(
  selectStore,
  (s) => s.liveDefinitions
);

export const selectDefinitionDiff = createSelector(
  selectLiveDefinitions,
  (records): DefinitionDiff =>
    diffDefinitions(CONFIG_DEFINITION_MANIFEST, records)
);

/**
 * True when the definitions read but this organization's values did not, so
 * the page must not claim that every key is taking the environment default.
 */
export const selectValuesUnavailable = createSelector(
  selectStore,
  (s) => s.valuesUnavailable
);

export const selectRootOrgStatus = createSelector(
  selectStore,
  (s): RootOrgStatus => s.rootOrgStatus
);

export const selectOrganizationName = createSelector(
  selectStore,
  (s) => s.organizationName
);

/**
 * Whether the page offers its mutation controls.
 *
 * `unknown` counts as allowed. The root-org check is advisory — the permission
 * model may not be exactly "root org only" everywhere — so an unconfirmed
 * session is left to try and to receive the API's own refusal on the row,
 * rather than facing a disabled button that explains nothing.
 */
export const selectCanMutateDefinitions = createSelector(
  selectRootOrgStatus,
  (status) => status !== 'notRoot'
);

export const selectPendingDefinitionKeys = createSelector(
  selectStore,
  (s) => s.pendingKeys
);

export const selectDefinitionErrors = createSelector(
  selectStore,
  (s) => s.errorsByKey
);

/** Transient per-key "this just worked", for the row to show in place. */
export const selectDefinitionSuccess = createSelector(
  selectStore,
  (s) => s.successByKey
);
